import { useState } from "react";
import { ArrowLeft, ArrowRight, Upload, X } from "lucide-react";
import { Alert, Button, FileButton, Spinner, Tag } from "../../../../ui";
import { useAppDispatch } from "../../../../store/store";
import { setGeneralAlertMessage } from "../../../../store/slices/generalSlice";
import { moveItem } from "../../shared/reorder";
import { useDragReorder } from "../../shared/useReorder";
import type { Combinations } from "../combinations";
import type { PhotoOffer } from "../editorModel";
import { uploadShopImage } from "../uploadShopImage";

/** Photo edits are functional, so uploads that finish together don't overwrite each other. */
export type UrlsUpdate = (urls: string[]) => string[];

export type StylePhotosProps = {
  styleKey: string;
  /** The style's label ("Black"); empty for a product with no values */
  title: string;
  urls: string[];
  /** Has a switched-on SKU and no photo */
  missing: boolean;
  /** A save error that names this style */
  error: string | null;
  onChange: (update: UrlsUpdate) => void;
  onBusyChange: (delta: 1 | -1) => void;
};

/** One style's photos: upload several, drag (or use the arrows) to reorder; the first is the cover. */
export function StylePhotos({ styleKey, title, urls, missing, error, onChange, onBusyChange }: StylePhotosProps) {
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(0);
  const drag = useDragReorder((from, to) => onChange((list) => moveItem(list, from, to)), () => undefined);

  const handleFiles = async (fileList: FileList) => {
    const files = Array.from(fileList);
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length < files.length) dispatch(setGeneralAlertMessage({ type: "error", message: "Please select image files only" }));
    for (const file of images) {
      setUploading((n) => n + 1);
      onBusyChange(1);
      try {
        const url = await uploadShopImage(file);
        onChange((list) => [...list, url]);
      } catch (err: any) {
        console.error("Upload error:", err);
        dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || `Failed to upload ${file.name}` }));
      } finally {
        setUploading((n) => n - 1);
        onBusyChange(-1);
      }
    }
  };

  const step = (index: number, direction: -1 | 1) => onChange((list) => moveItem(list, index, index + direction));
  const remove = (index: number) => onChange((list) => list.filter((_, i) => i !== index));

  return (
    <div className={`style-photos${error ? " has-error" : ""}`} data-target={`style:${styleKey}`}>
      {(title || missing) && (
        <div className="style-photos__head">
          {title && <span className="style-photos__title">{title}</span>}
          {missing && <Tag tone="warn">Missing photo</Tag>}
        </div>
      )}
      <div className="image-grid">
        {urls.map((url, index) => (
          <div
            key={url}
            className={`image-tile${drag.dragIndex === index ? " reorder-dragging" : ""}`}
            {...drag.handleProps(index)}
            {...drag.rowProps(index)}
          >
            <img src={url} alt={`${title || "Product"} ${index + 1}`} draggable={false} />
            {index === 0 && <span className="cover-tag">Cover</span>}
            <button type="button" className="remove-image-btn" onClick={() => remove(index)} data-tooltip="Remove image" aria-label="Remove image">
              <X size={12} />
            </button>
            <div className="image-move">
              <button type="button" onClick={() => step(index, -1)} disabled={index === 0} data-tooltip="Move left" aria-label="Move left">
                <ArrowLeft size={12} />
              </button>
              <button
                type="button"
                onClick={() => step(index, 1)}
                disabled={index === urls.length - 1}
                data-tooltip="Move right"
                aria-label="Move right"
              >
                <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
        {Array.from({ length: uploading }).map((_, i) => (
          <div key={`uploading-${i}`} className="image-tile uploading">
            <Spinner size="sm" label="Uploading" />
          </div>
        ))}
      </div>
      <FileButton
        variant="tint"
        size="sm"
        className="add-images-btn"
        icon={<Upload size={14} />}
        multiple
        accept="image/png,image/jpeg,image/webp"
        onFiles={handleFiles}
      >
        Add images
      </FileButton>
      {error && (
        <p className="ui-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export type PhotosSectionProps = {
  combos: Combinations;
  images: Record<string, string[]>;
  missing: Set<string>;
  /** Photos of styles that are gone, each offered to the new style that contains their values */
  offers: PhotoOffer[];
  errorAt: (id: string) => string | null;
  onChange: (styleKey: string, update: UrlsUpdate) => void;
  onUseOffer: (offer: PhotoOffer) => void;
  onBusyChange: (delta: 1 | -1) => void;
};

/** Editor §4: one uploader for a simple product, one per style ("Black", "White") with variants. */
export function PhotosSection({ combos, images, missing, offers, errorAt, onChange, onUseOffer, onBusyChange }: PhotosSectionProps) {
  const perStyle = combos.styles.length > 1 || !!combos.styles[0]?.label;
  const sectionError = errorAt("section:photos");
  return (
    <section className="editor-section" data-target="section:photos">
      <h3 className="editor-section__title">Photos</h3>
      <p className="editor-hint">
        {perStyle
          ? "Every colour or style gets its own photos; its sizes share them. The first photo is the cover. Drag to reorder."
          : "The first photo is the cover. Drag to reorder."}
      </p>
      {offers.map((offer) => {
        const one = offer.urls.length === 1;
        return (
          <Alert
            key={`${offer.fromLabel}>${offer.toKey}`}
            tone="info"
            action={
              <Button size="sm" variant="tint" onClick={() => onUseOffer(offer)}>
                Use for {offer.toLabel || "this style"}
              </Button>
            }
          >
            {offer.urls.length} {offer.fromLabel ? `${one ? "photo" : "photos"} of ${offer.fromLabel}` : `earlier ${one ? "photo" : "photos"}`}{" "}
            {one ? "isn't" : "aren't"} linked to a style below and {one ? "is" : "are"} dropped on save.
          </Alert>
        );
      })}
      {combos.tooMany ? (
        <Alert tone="warn">Remove some values or sizes to see the photo sets.</Alert>
      ) : (
        combos.styles.map((style) => (
          <StylePhotos
            key={style.key}
            styleKey={style.key}
            title={style.label}
            urls={images[style.key] ?? []}
            missing={missing.has(style.key)}
            error={errorAt(`style:${style.key}`)}
            onChange={(update) => onChange(style.key, update)}
            onBusyChange={onBusyChange}
          />
        ))
      )}
      {sectionError && <Alert tone="danger">{sectionError}</Alert>}
    </section>
  );
}

export default PhotosSection;
